import React, { useState } from "react";
import {
  Box,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Button,
  IconButton,
  Link as MUILink,
  Chip,
} from "@mui/material";
import { User, Settings, LogOut, Rocket, LucideIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import ActionButton from "../atoms/ActionButton";
import { useUI } from "@/context/UIContext";

interface MenuItemConfig {
  id: string;
  label: string;
  icon: LucideIcon;
  action: () => void;
  component?: any;
}

interface MenuSection {
  items: MenuItemConfig[];
}

interface ProfileMenuProps {
  user: any;
  profile: any;
  userAvatar: string;
  userPreferredName: string;
  onLogout: () => void;
}

export const ProfileMenu: React.FC<ProfileMenuProps> = ({
  user,
  profile,
  userAvatar,
  userPreferredName,
  onLogout,
}) => {
  const router = useRouter();
  const { showOverlay } = useUI();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleUpgradeClick = () => {
    showOverlay({
      type: "dialog",
      component: "BetaUpgradeMessage",
      title: "Upgrade",
    });
  };

  // Menu configuration - business logic separated from presentation
  const menuSections: MenuSection[] = [
    {
      items: [
        {
          id: "profile",
          label: "Profile",
          icon: User,
          action: () => {
            handleMenuClose();
            router.push("/account/profile");
          },
          component: MUILink,
        },
        {
          id: "settings",
          label: "Settings",
          icon: Settings,
          action: () => {
            handleMenuClose();
            router.push("/account/settings");
          },
          component: MUILink,
        },
      ],
    },
    {
      items: [
        {
          id: "logout",
          label: "Logout",
          icon: LogOut,
          action: () => {
            handleMenuClose();
            onLogout();
          },
        },
      ],
    },
  ];

  const getPlanDisplayName = (plan: string): string => {
    const planMap: Record<string, string> = {
      free: "Free plan",
      starter: "Starter plan",
      pro: "Pro plan",
    };
    return planMap[plan] || `${plan} plan`;
  };

  const getUserDisplayName = (): string => {
    // Use stage_name or legal_name from profile
    if (userPreferredName) {
      return userPreferredName;
    }
    // Fallback to email prefix (before @), max 16 characters
    if (user?.email) {
      const emailPrefix = user.email.split('@')[0];
      return emailPrefix.length > 16 ? emailPrefix.substring(0, 16) : emailPrefix;
    }
    return 'User';
  };

  return (
    <>
      <IconButton
        onClick={handleMenuClick}
        color="inherit"
        sx={{
          width: "40px",
          height: "40px",
          position: "relative",
          overflow: "hidden",
          padding: "0px",
          borderRadius: "100%",
        }}
      >
        <Box
          className="profile-avatar"
          sx={{
            width: "100%",
            height: "100%",
            overflow: "hidden",
            borderRadius: "100%",
            backgroundImage: `url(${userAvatar})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      </IconButton>

      <Menu
        className="profile-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1.5,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.25)",
            borderRadius: 2.5,
            width: 260,
            overflow: "hidden",
            backgroundColor: "#1a1f35",
            border: "1px solid rgba(255, 255, 255, 0.1)",
          },
        }}
        MenuListProps={{
          sx: {
            py: 0,
          },
        }}
      >
        {/* User Info Header */}
        <Box
          sx={{
            padding: "16px 16px 12px 16px",
            textAlign: "left",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            className="profile-avatar"
            sx={{
              width: "48px",
              height: "48px",
              position: "relative",
              overflow: "hidden",
              borderRadius: "100%",
              flexShrink: 0,
              backgroundImage: `url(${userAvatar})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: "0.9375rem",
                lineHeight: 1.3,
                mb: 0.75,
                color: "#ffffff",
              }}
            >
              {getUserDisplayName()}
            </Typography>
            <Chip
              label={profile?.subscription_plan ? getPlanDisplayName(profile.subscription_plan) : "Free plan"}
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.75rem',
                height: '24px',
                border: 'none',
                cursor: 'default',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                },
              }}
            />
          </Box>
        </Box>

        {/* Upgrade Button */}
        <Box
          sx={{
            px: 2,
            pb: 1.5,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <ActionButton
            text="Upgrade"
            icon={<Rocket size={16} />}
            variant="contained"
            size="small"
            fullWidth
            onClick={handleUpgradeClick}
            sx={{
              fontSize: "0.8125rem",
              py: 0.75,
            }}
          />
        </Box>

        <Divider />

        {/* Menu Sections */}
        {menuSections.map((section, sectionIndex) => (
          <React.Fragment key={sectionIndex}>
            <Box>
              {section.items.map((item) => {
                const IconComponent = item.icon;
                return (
                  <MenuItem
                    key={item.id}
                    sx={{
                      px: 2,
                      py: 1.25,
                      gap: 1.5,
                      borderRadius: 0,
                      "&:hover": {
                        backgroundColor: "action.hover",
                        "& svg": {
                          color: "text.primary",
                        },
                      },
                      "& svg": {
                        color: "rgba(255, 255, 255, 0.7)",
                      },
                    }}
                    component={item.component}
                    onClick={item.action}
                  >
                    <IconComponent size={18} />
                    <Typography variant="body2" sx={{ fontSize: "0.9375rem", color: "rgba(255, 255, 255, 0.7)" }}>
                      {item.label}
                    </Typography>
                  </MenuItem>
                );
              })}
            </Box>
            {sectionIndex < menuSections.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Menu>
    </>
  );
};
